import { useContext, useMemo } from 'react';
import { FeatureAppContext } from '../context/FeatureAppContext';
import type { Breakpoint } from './useBreakpoint';
import { getAbsoluteUrl } from './utils';

export interface TrackingManager {
  ready(version: string): void;
  click(
    linkType: 'link' | 'cta' | 'arrow',
    label: string,
    targetURL: string,
    pos: string,
    clickID: Breakpoint,
    value?: string,
    linkCount?: number,
    ctaCount?: number,
  ): void;
  navigation(isActive: boolean, clickID: Breakpoint, linkCount: number, ctaCount: number): void;
  secondaryNavigation(isActive: boolean, clickID: Breakpoint, name?: string): void;
}

export interface TrackingEvent {
  event: {
    eventInfo: {
      eventAction: string;
      eventName: string;
    };
    attributes: Record<string, unknown>;
  };
}

function createTrackingPayload(
  action: string,
  name: string,
  attributes: Record<string, unknown>,
): TrackingEvent {
  const { host, pathname } = document.location;
  const defaultAttributes = {
    componentName: 'subpage-navigation',
    currentURL: `${host}${pathname}`,
  };
  return {
    event: {
      eventInfo: {
        eventAction: action,
        eventName: name,
      },
      attributes: {
        ...defaultAttributes,
        ...attributes,
      },
    },
  };
}

function createComponentPayload(version: string) {
  return {
    componentInfo: {
      implementer: 2,
      version,
    },
  };
}

export function useTrackingManager(): TrackingManager {
  const { trackingService, logger } = useContext(FeatureAppContext);

  return useMemo((): TrackingManager => {
    function track(...args: Parameters<typeof createTrackingPayload>): void {
      if (trackingService?.evaluateLinkType) {
        const payload = createTrackingPayload(...args);
        logger.debug('Sending tracking event', payload);
        trackingService.track(payload);
      }
    }

    function trackWithComponent(
      version: string,
      ...args: Parameters<typeof createTrackingPayload>
    ): void {
      if (trackingService) {
        const payload = createTrackingPayload(...args);
        const componentPayload = createComponentPayload(version);
        if (logger) logger.debug('Sending component info', componentPayload);
        trackingService.track({ ...payload, componentUpdate: componentPayload });
      }
    }
    return {
      ready(version) {
        trackWithComponent(version, 'feature_app_ready', 'subpage navigation - feature app ready', {
          label: '',
          targetURL: '',
          clickID: '',
          elementName: '',
          value: '',
          pos: '',
        });
      },
      click(linkType, label, targetURL, pos, clickID, value, linkCount, ctaCount) {
        let evaluatedLinkType: string;
        if (linkType === 'arrow') {
          evaluatedLinkType = 'navigation';
        } else {
          evaluatedLinkType = trackingService?.evaluateLinkType?.(targetURL) || 'unknown';
        }
        let elementName: string;
        if (linkType === 'link') {
          elementName = 'text link';
        } else if (linkType === 'cta') {
          elementName = 'button';
        } else {
          elementName = 'arrow';
        }
        const url = getAbsoluteUrl(targetURL);
        track(evaluatedLinkType, `subpage navigation - click on ${linkType}`, {
          label,
          targetURL: url,
          clickID,
          elementName,
          value,
          pos: linkType === 'arrow' ? `${linkCount}~${ctaCount}` : pos,
        });
      },

      navigation(isActive, clickID, linkCount, ctaCount) {
        const action = isActive ? 'close' : 'open';
        track('navigation', `subpage navigation - ${action} navigation`, {
          label: '',
          targetURL: '',
          clickID,
          elementName: 'drop down',
          value: '',
          pos: `${linkCount}~${ctaCount}`,
        });
      },

      secondaryNavigation(isPopOver, clickID, name) {
        const action = isPopOver ? 'open' : 'close';
        track('navigation', `subpage navigation - ${action} vtp link popover`, {
          label: name,
          targetURL: '',
          clickID,
          elementName: 'drop down',
          value: '2',
          pos: '',
        });
      },
    };
  }, [trackingService]);
}
